import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { AntimalwareSettingsExclusionsItem, ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  AntimalwareExclusionsFormValue,
  AntimalwareExclusionsSearchableKeywords,
  AntimalwareVendorsExclusionsUiType,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  ANTIMALWARE_EXCLUSIONS_DEFAULT_VALUE,
  getAntimalwareExclusionsSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { Feature } from '@nirvana/shared/constants';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzAntimalwareExclusionsGridWebComponent } from '../gridComponents/inPolicyExclusions/GzAntimalwareExclusionsGridWebComponent';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import {
  getIsAntimalwareSettingsExclusionsListsLoaded,
  getIsAntimalwareSettingsVendorsExclusionsListsLoaded,
} from '../../../../redux/customStates/antimalware/antimalware.actions';
import { GzAntimalwareExclusionsListsWebComponent } from '../gridComponents/listsExclusions';
import { GzAntimalwareVendorsExclusionsListsWebComponent } from '../gridComponents/vendorsListsExclusions';
import { classMap } from 'lit/directives/class-map.js';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS)
export class GzAntimalwareExclusionsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareExclusionsFormValue,
  AntimalwareExclusionsSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.EXCLUSIONS,
  };
  protected searchableKeywords = getAntimalwareExclusionsSearchableKeywords();

  @query('#antimalware-exclusions-enable-field')
  private exclusionsEnableField: GzFormFieldWebComponent<GzToggleWebComponent>;
  @query('#antimalware-exclusions-field')
  private exclusionsField: GzFormFieldWebComponent<
    GzAntimalwareExclusionsGridWebComponent,
    AntimalwareSettingsExclusionsItem[]
  >;

  @query('#antimalware-exclusions-lists-enable-field')
  private exclusionsListsEnableField: GzFormFieldWebComponent<GzToggleWebComponent>;
  @query('#antimalware-exclusions-lists-field')
  private exclusionsListsField: GzFormFieldWebComponent<GzAntimalwareExclusionsListsWebComponent, string[]>;

  @query('#antimalware-use-built-in-exclusions-enable-field')
  private useBuiltInExclusionsEnableField: GzFormFieldWebComponent<GzToggleWebComponent>;
  @query('#antimalware-vendors-exclusions-lists-type-field')
  private vendorsExclusionsListsTypeRadio: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#antimalware-vendors-exclusions-lists-field')
  private vendorsExclusionsListsField: GzFormFieldWebComponent<GzAntimalwareVendorsExclusionsListsWebComponent>;

  private isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.Exclusions].label}"
      ><span>${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.Exclusions].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
      >${translateFn(
        'Create exclusions specifically for this policy, assign them from configuration profiles, or use the exclusions recommended by Bitdefender.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="mt-4">
        <gz-form-field id="antimalware-exclusions-enable-field" class="mbw-1">
          <gz-toggle gz-form-input @toggleChange="${e => toggleVisibility(this.exclusionsField, e.detail.checked)}">
            <gz-title
              fontSize="11"
              fontWeight="600"
              class="toggle-subtitle"
              searchable-label="${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.InPolicyExclusions]
                .label}"
              >${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.InPolicyExclusions]
                .translatedLabel}</gz-title
            >
          </gz-toggle>
        </gz-form-field>

        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
          >${translateFn('Create and configure exclusions specific to this policy.')}
        </gz-text>

        <gz-form-field id="antimalware-exclusions-field">
          <gz-policy-panel-antimalware-exclusions-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-antimalware-exclusions-grid>
        </gz-form-field>
      </div>

      <div class="mt-4">
        <gz-form-field id="antimalware-exclusions-lists-enable-field" class="mbw-1">
          <gz-toggle
            gz-form-input
            @toggleChange="${e => toggleVisibility(this.exclusionsListsField, e.detail.checked)}"
          >
            <gz-title
              fontSize="11"
              fontWeight="600"
              class="toggle-subtitle"
              searchable-label="${this.searchableKeywords[
                AntimalwareExclusionsSearchableKeywords.ExclusionsFromConfigurationProfiles
              ].label}"
              >${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.ExclusionsFromConfigurationProfiles]
                .translatedLabel}</gz-title
            >
          </gz-toggle>
        </gz-form-field>

        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
          >${translateFn('Assign exclusion lists from configuration profiles to this policy.')}
        </gz-text>

        <gz-form-field id="antimalware-exclusions-lists-field" class="mt-2">
          <gz-policy-panel-antimalware-exclusions-lists gz-form-input></gz-policy-panel-antimalware-exclusions-lists>
        </gz-form-field>
      </div>

      <div class="mt-4 ${classMap({ hidden: this.isCompanionStandalone })}">
        <gz-form-field id="antimalware-use-built-in-exclusions-enable-field" class="mbw-1">
          <gz-toggle gz-form-input @toggleChange="${this.onUseBuiltInEnableExclusionsChange}">
            <gz-title
              fontSize="11"
              fontWeight="600"
              class="toggle-subtitle"
              searchable-label="${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.VendorExclusions]
                .label}"
              >${this.searchableKeywords[AntimalwareExclusionsSearchableKeywords.VendorExclusions]
                .translatedLabel}</gz-title
            >
          </gz-toggle>
        </gz-form-field>

        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
          >${translateFn(
            'When this option is enabled, all vendor and product exclusions recommended by Bitdefender are applied. To apply specific exclusions, select them from the list below.'
          )}
        </gz-text>

        <div class="mt-2">
          <gz-form-field id="antimalware-vendors-exclusions-lists-type-field" class="mbw-1">
            <gz-radio-group
              gz-form-input
              .selectedOption="${AntimalwareVendorsExclusionsUiType.ALL}"
              @radioSelectionChanged=${e => this.onVendorsExclusionsTypeChanged(e.detail.selectedValue)}
            >
              <gz-radio
                label="${translateFn('All vendor and product exclusions')}"
                .value="${AntimalwareVendorsExclusionsUiType.ALL}"
              ></gz-radio>
              <gz-radio
                label="${translateFn('Custom')}"
                .value="${AntimalwareVendorsExclusionsUiType.CUSTOM}"
              ></gz-radio>
            </gz-radio-group>
          </gz-form-field>

          <gz-form-field id="antimalware-vendors-exclusions-lists-field">
            <gz-policy-panel-antimalware-vendors-exclusions-lists
              gz-form-input
            ></gz-policy-panel-antimalware-vendors-exclusions-lists>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private onUseBuiltInEnableExclusionsChange(e: CustomEvent) {
    const value = e.detail.checked;
    if (!value) {
      this.vendorsExclusionsListsField.value = [];
      this.vendorsExclusionsListsTypeRadio.value = AntimalwareVendorsExclusionsUiType.ALL;
    }

    toggleVisibility(this.vendorsExclusionsListsField, value);
    toggleVisibility(this.vendorsExclusionsListsTypeRadio, value);
  }

  private onVendorsExclusionsTypeChanged(selectedValue: AntimalwareVendorsExclusionsUiType) {
    switch (selectedValue) {
      case AntimalwareVendorsExclusionsUiType.ALL:
        this.vendorsExclusionsListsField.Validators = [];
        this.vendorsExclusionsListsField.value = [];
        toggleDisabled(this.vendorsExclusionsListsField, true);
        break;
      case AntimalwareVendorsExclusionsUiType.CUSTOM:
        this.vendorsExclusionsListsField.Validators = [RequiredValidator(translateFn('Required field'))];
        toggleDisabled(this.vendorsExclusionsListsField, false);
        break;
    }
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.securityServers
   */
  protected getValueFromManager(): AntimalwareExclusionsFormValue {
    // get default value
    const defaultValue = ANTIMALWARE_EXCLUSIONS_DEFAULT_VALUE;

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          settings: {
            activateExclusions: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.activateExclusions',
              {}
            ),
            useBuiltInExclusions: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.useBuiltInExclusions',
              true
            ),
          },
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Get additional information from manager and set missing fields.
   * Fields used in this section:
   * - antimalware.exclusionsLists
   */
  protected getAdditionalInfoFromManager() {
    // get default value
    const defaultAdditionalInfo = {
      antimalware: {
        exclusionsLists: [],
        vendorsExclusionsLists: [],
      },
    };

    // get value from manager
    const additionalInfo = {
      antimalware: {
        exclusionsLists: PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
          ['antimalware', 'exclusionsLists'],
          []
        ),
        vendorsExclusionsLists: PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
          ['antimalware', 'vendorsExclusionsLists'],
          []
        ),
      },
    };

    // merge received value with default one
    return deepmerge(defaultAdditionalInfo, additionalInfo);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          settings: {
            useBuiltInExclusions: this.useBuiltInExclusionsEnableField,
            activateExclusions: {
              enable: this.exclusionsEnableField,
              exclusionsItems: this.exclusionsField,
              useExclusionLists: this.exclusionsListsEnableField,
              exclusionsLists: this.exclusionsListsField,
              selectedVendorExclusionLists: this.vendorsExclusionsListsField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    this.exclusionsListsField.Input.lists = this.additionalInfo.antimalware.exclusionsLists;
    this.vendorsExclusionsListsField.Input.lists = this.additionalInfo.antimalware.vendorsExclusionsLists;

    // set radio group value
    this.vendorsExclusionsListsTypeRadio.value = PolicyPanelManager.getInstance().getAdditionalPolicyInfo(
      ['valueHistory', 'antimalware', 'vendorsExclusionsType'],
      AntimalwareVendorsExclusionsUiType.ALL
    );

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          settings: {
            useBuiltInExclusions: this.valueT.uiSettings.antimalware.settings.useBuiltInExclusions,
            activateExclusions: {
              enable: this.valueT.uiSettings.antimalware.settings.activateExclusions.enable,
              exclusionsItems: this.valueT.uiSettings.antimalware.settings.activateExclusions.exclusionsItems,
              useExclusionLists: this.valueT.uiSettings.antimalware.settings.activateExclusions.useExclusionLists,
              exclusionsLists: this.valueT.uiSettings.antimalware.settings.activateExclusions.exclusionsLists,
              selectedVendorExclusionLists:
                this.valueT.uiSettings.antimalware.settings.activateExclusions.selectedVendorExclusionLists,
            },
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // set unused field
    _.set(value, ['uiSettings', 'antimalware', 'settings', 'activateExclusions', 'useVendorExclusionLists'], false);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'settings'], {});
    }

    return value;
  }

  /**
   * Send additional information to manager.
   */
  protected sendAdditionalInfoToManagerIfNeeded(): void {
    PolicyPanelManager.getInstance().setAdditionalPolicyInfoToPath(
      ['valueHistory', 'antimalware', 'vendorsExclusionsType'],
      this.vendorsExclusionsListsTypeRadio.value
    );
  }

  /**
   * Override method to check if additional info is loaded.
   * @param policiesApp
   */
  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return (
      getIsAntimalwareSettingsExclusionsListsLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED &&
      getIsAntimalwareSettingsVendorsExclusionsListsLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS: GzAntimalwareExclusionsWebComponent;
  }
}
